import React, { VoidFunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ConfirmModal,
  ConfirmModalProps
} from 'record-it-components/modal/ConfirmModal/ConfirmModal';
import { ConfirmModalHeader } from 'record-it-components/modal/ConfirmModalHeader/ConfirmModalHeader';
import { ConfirmModalBody } from 'record-it-components/modal/ConfirmModalBody/ConfirmModalBody';
import { ConfirmModalButtonFooter } from 'record-it-components/modal/ConfirmModalButtonFooter/ConfirmModalButtonFooter';
import {
  TextInput,
  TextInputType
} from 'record-it-components/input/TextInput/TextInput';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { MessageBoxType } from 'record-it-components/misc/MessageBox/MessageBox';
import { MessageListBox } from 'record-it-components/misc/MessageListBox/MessageListBox';
import { MessageListBoxListItem } from 'record-it-components/misc/MessageListBoxListItem/MessageListBoxListItem';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import styles from './CreateUserDialog.module.css';
import {
  ButtonStandard,
  ButtonStandardType
} from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { ApiAdapter } from '../../../classes/ApiAdapter/ApiAdapter';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { EntityName } from 'record-it-time-common/EntityName';

export type CreateUserDialogProps = PropsWithoutChildren<ConfirmModalProps> & {
  onCancel: () => void;
  onUserCreated: () => void;
};

export const CreateUserDialog: VoidFunctionComponent<CreateUserDialogProps> =
  React.memo((props) => {
    const { onCancel, onUserCreated, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const [failedToCreateUser, setFailedToCreateUser] =
      useState<boolean>(false);
    const [noEmailProvided, setNoEmailProvided] = useState<boolean>(false);
    const [noPasswordProvided, setNoPasswordProvided] =
      useState<boolean>(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const { t } = useTranslation();
    const userRepository = useEntityRepository(EntityName.User);

    const validate = (): boolean => {
      let valid = true;

      if (!email.trim()) {
        setNoEmailProvided(true);
        valid = false;
      } else {
        setNoEmailProvided(false);
      }

      if (!password) {
        setNoPasswordProvided(true);
        valid = false;
      } else {
        setNoPasswordProvided(false);
      }

      return valid;
    };

    const handleCreateClick = useEventCallback(() => {
      if (!validate()) {
        return;
      }

      setFailedToCreateUser(false);
      setNoEmailProvided(false);
      setNoPasswordProvided(false);

      void ApiAdapter.createUser({
        email,
        password
      })
        .then((response) => {
          if (response.success) {
            userRepository.addExternalEntityLocally(response.user);
            onUserCreated();
          } else {
            setFailedToCreateUser(true);
          }
        })
        .catch((e) => {
          console.error(e);
          setFailedToCreateUser(true);
        });
    });

    const errorMessages = [
      ...(failedToCreateUser
        ? [t('components.user.CreateUserDialog.failedToCreateUser')]
        : [])
    ];
    const warningMessages = [
      ...(noEmailProvided
        ? [t('components.user.CreateUserDialog.noEmailProvided')]
        : []),
      ...(noPasswordProvided
        ? [t('components.user.CreateUserDialog.noPasswordProvided')]
        : [])
    ];

    return (
      <ConfirmModal {...remainingProps}>
        <ConfirmModalHeader>
          {t('components.user.CreateUserDialog.title')}
        </ConfirmModalHeader>
        <ConfirmModalBody>
          {errorMessages.length ? (
            <MessageListBox
              type={MessageBoxType.Error}
              data-testid="CreateUserDialog.ErrorBox"
              className={styles.messageListBox}
            >
              {errorMessages.map((message) => (
                <MessageListBoxListItem key={message}>
                  {message}
                </MessageListBoxListItem>
              ))}
            </MessageListBox>
          ) : null}
          {warningMessages.length ? (
            <MessageListBox
              type={MessageBoxType.Warning}
              data-testid="CreateUserDialog.WarningBox"
              className={styles.messageListBox}
            >
              {warningMessages.map((message) => (
                <MessageListBoxListItem key={message}>
                  {message}
                </MessageListBoxListItem>
              ))}
            </MessageListBox>
          ) : null}
          <FormGroup>
            <TextInput
              label={t('entities.User.email')}
              type={TextInputType.Email}
              value={email}
              onInput={setEmail}
              data-testid="CreateUserDialog.EmailInput"
            ></TextInput>
          </FormGroup>
          <FormGroup>
            <TextInput
              label={t('entities.User.password')}
              type={TextInputType.Password}
              value={password}
              onInput={setPassword}
              data-testid="CreateUserDialog.PasswordInput"
            ></TextInput>
          </FormGroup>
        </ConfirmModalBody>
        <ConfirmModalButtonFooter>
          <ButtonStandard
            type={ButtonStandardType.Primary}
            onClick={handleCreateClick}
            data-testid="CreateUserDialog.CreateButton"
          >
            {t('components.user.CreateUserDialog.create')}
          </ButtonStandard>
          <ButtonStandard
            type={ButtonStandardType.Secondary}
            onClick={onCancel}
            data-testid="CreateUserDialog.CancelButton"
          >
            {t('components.user.CreateUserDialog.cancel')}
          </ButtonStandard>
        </ConfirmModalButtonFooter>
      </ConfirmModal>
    );
  });
